import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, CheckCircle, XCircle, Unlock, AlertTriangle, Eye, FileText } from 'lucide-react';
import { FilterSection, LoadingState, EmptyState, useToast } from '../components/ui';
import api from '../services/api';

const QCInspectionReview = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [filters, setFilters] = useState({
    search: '',
    result: 'all',
    form_105_status: 'all'
  });

  const [selectedInspection, setSelectedInspection] = useState(null);

  // Fetch QC Inspections
  const { data: inspectionsData, isLoading } = useQuery({
    queryKey: ['qc-inspections', filters],
    queryFn: async () => {
      const params = {};
      if (filters.result !== 'all') params.result = filters.result;

      const response = await api.get('/qc/inspections/', { params });
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const inspections = inspectionsData?.results || inspectionsData || [];

  // Apply client-side filters
  const filteredInspections = inspections.filter(inspection => {
    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchesSearch =
        inspection.inspection_number.toLowerCase().includes(search) ||
        inspection.material_code?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Form 105 status filter
    if (filters.form_105_status === 'generated' && !inspection.form_105_generated) {
      return false;
    }
    if (filters.form_105_status === 'pending' && inspection.form_105_generated) {
      return false;
    }

    return true;
  });

  // Generate Form 105 Mutation
  const generateForm105Mutation = useMutation({
    mutationFn: async (inspectionId) => {
      const response = await api.post(`/qc/inspections/${inspectionId}/generate-form-105/`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['qc-inspections']);
      queryClient.invalidateQueries(['grns']);
      success(`Form 105 generated successfully: ${data.form_105_grn}`);
      setSelectedInspection(null);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to generate Form 105');
    }
  });

  const handleGenerateForm105 = (inspection) => {
    if (window.confirm(`Generate Form 105 release GRN for ${inspection.inspection_number}?\n\nThis will release ${inspection.accepted_quantity} units from blocked stock to unrestricted stock.`)) {
      generateForm105Mutation.mutate(inspection.id);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      result: 'all',
      form_105_status: 'all'
    });
  };

  const getResultBadge = (result) => {
    const resultColors = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'PASS': 'bg-green-100 text-green-800',
      'FAIL': 'bg-red-100 text-red-800',
      'QUANTITY_VARIANCE': 'bg-orange-100 text-orange-800',
      'CONDITIONAL_PASS': 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${resultColors[result] || 'bg-gray-100 text-gray-800'}`}>
        {result}
      </span>
    );
  };

  const stats = [
    {
      label: 'Total Inspections',
      value: filteredInspections.length,
      color: 'bg-blue-500'
    },
    {
      label: 'Passed',
      value: filteredInspections.filter(i => i.result === 'PASS' || i.result === 'CONDITIONAL_PASS').length,
      color: 'bg-green-500'
    },
    {
      label: 'Awaiting Form 105',
      value: filteredInspections.filter(i => i.can_generate_form_105).length,
      color: 'bg-orange-500'
    },
    {
      label: 'Form 105 Generated',
      value: filteredInspections.filter(i => i.form_105_generated).length,
      color: 'bg-purple-500'
    }
  ];

  const filterOptions = [
    {
      name: 'result',
      label: 'Result',
      value: filters.result,
      options: [
        { value: 'PASS', label: 'Pass' },
        { value: 'FAIL', label: 'Fail' },
        { value: 'CONDITIONAL_PASS', label: 'Conditional Pass' },
        { value: 'QUANTITY_VARIANCE', label: 'Quantity Variance' },
        { value: 'PENDING', label: 'Pending' }
      ]
    },
    {
      name: 'form_105_status',
      label: 'Form 105 Status',
      value: filters.form_105_status,
      options: [
        { value: 'pending', label: 'Awaiting Generation' },
        { value: 'generated', label: 'Generated' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">QC Inspection Review</h1>
        <p className="text-gray-600 mt-2">Review inspections and generate Form 105 releases</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} text-white p-3 rounded-lg`}>
                <ClipboardCheck className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterSection
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchValue={filters.search}
        searchPlaceholder="Search by inspection number, material..."
      />

      {/* Inspections List */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">QC Inspections</h2>

        {isLoading ? (
          <LoadingState message="Loading inspections..." />
        ) : filteredInspections.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title="No inspections found"
            message={filters.search || filters.result !== 'all'
              ? "Try adjusting your filters"
              : "No inspections completed yet"}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Inspection Number</th>
                  <th className="text-left p-3">Material</th>
                  <th className="text-left p-3">Inspector</th>
                  <th className="text-left p-3">Inspected Qty</th>
                  <th className="text-left p-3">Accepted / Rejected</th>
                  <th className="text-left p-3">Result</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Form 105 Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInspections.map((inspection) => (
                  <tr key={inspection.id} className="border-b hover:bg-gray-50 animate-fade-in">
                    <td className="p-3">
                      <div className="font-semibold text-nexgensis-primary">
                        {inspection.inspection_number}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{inspection.material_code}</div>
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {inspection.inspector}
                    </td>
                    <td className="p-3 font-medium">
                      {parseFloat(inspection.inspected_quantity).toFixed(2)}
                    </td>
                    <td className="p-3">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-600 font-medium">
                            {parseFloat(inspection.accepted_quantity || 0).toFixed(2)}
                          </span>
                        </div>
                        {parseFloat(inspection.rejected_quantity || 0) > 0 && (
                          <div className="flex items-center space-x-2">
                            <XCircle className="w-4 h-4 text-red-600" />
                            <span className="text-sm text-red-600 font-medium">
                              {parseFloat(inspection.rejected_quantity).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {getResultBadge(inspection.result)}
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {new Date(inspection.inspection_date).toLocaleString()}
                    </td>
                    <td className="p-3">
                      {inspection.form_105_generated ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Generated
                        </span>
                      ) : inspection.can_generate_form_105 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          N/A
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedInspection(inspection)}
                          className="btn-secondary text-sm inline-flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        {inspection.can_generate_form_105 && !generateForm105Mutation.isLoading && (
                          <button
                            onClick={() => handleGenerateForm105(inspection)}
                            className="btn-primary text-sm inline-flex items-center"
                          >
                            <Unlock className="w-4 h-4 mr-1" />
                            Generate Form 105
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inspection Detail Modal */}
      {selectedInspection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">{selectedInspection.inspection_number}</h2>
                <p className="text-gray-600 mt-1">{selectedInspection.material_code}</p>
              </div>
              <div>
                {getResultBadge(selectedInspection.result)}
              </div>
            </div>

            {/* Inspection Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-gray-700">Inspector</label>
                <p className="text-gray-900">{selectedInspection.inspector}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Inspection Date</label>
                <p className="text-gray-900">{new Date(selectedInspection.inspection_date).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Sample Size</label>
                <p className="text-gray-900 font-semibold">{selectedInspection.sample_size}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Inspected Quantity</label>
                <p className="text-gray-900 font-semibold">{parseFloat(selectedInspection.inspected_quantity).toFixed(3)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Accepted Quantity</label>
                <p className="text-gray-900 font-semibold text-green-600">{parseFloat(selectedInspection.accepted_quantity || 0).toFixed(3)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Rejected Quantity</label>
                <p className="text-gray-900 font-semibold text-red-600">{parseFloat(selectedInspection.rejected_quantity || 0).toFixed(3)}</p>
              </div>
              {selectedInspection.defects_found > 0 && (
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">Defects Found</label>
                  <p className="text-gray-900">{selectedInspection.defects_found}</p>
                  {selectedInspection.defect_description && (
                    <p className="text-sm text-gray-600 mt-1">{selectedInspection.defect_description}</p>
                  )}
                </div>
              )}
              {selectedInspection.remarks && (
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">Remarks</label>
                  <p className="text-gray-900">{selectedInspection.remarks}</p>
                </div>
              )}
              {selectedInspection.approved_by && (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Approved By</label>
                    <p className="text-gray-900">{selectedInspection.approved_by}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Approved At</label>
                    <p className="text-gray-900">{new Date(selectedInspection.approved_at).toLocaleString()}</p>
                  </div>
                </>
              )}
            </div>

            {/* Form 105 Status */}
            <div className={`p-4 rounded-lg mb-6 ${
              selectedInspection.form_105_generated ? 'bg-green-50 border border-green-200' :
              selectedInspection.can_generate_form_105 ? 'bg-orange-50 border border-orange-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Form 105 Release Status</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedInspection.form_105_generated
                      ? 'Form 105 has been generated. Material released to unrestricted stock.'
                      : selectedInspection.can_generate_form_105
                      ? `Ready to generate Form 105. ${selectedInspection.accepted_quantity} units will be released.`
                      : 'Form 105 cannot be generated (inspection pending, not approved, or no accepted quantity).'}
                  </p>
                </div>
                {selectedInspection.can_generate_form_105 && !selectedInspection.form_105_generated && (
                  <button
                    onClick={() => handleGenerateForm105(selectedInspection)}
                    className="btn-primary inline-flex items-center"
                    disabled={generateForm105Mutation.isLoading}
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    {generateForm105Mutation.isLoading ? 'Generating...' : 'Generate Form 105'}
                  </button>
                )}
                {selectedInspection.form_105_generated && (
                  <span className="inline-flex items-center px-3 py-2 rounded-lg bg-green-100 text-green-800 font-semibold">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Released
                  </span>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setSelectedInspection(null)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCInspectionReview;
